<template>
    <div
        class="input-range"
        :class="classInputRange"
    >
        <div class="track-container">
            <div
                class="clamp-spacer left"
                :style="styleClampSpacerLeft"
            ></div>

            <div
                class="input-wrapper"
                :class="classTwoKnobbed"
            >
                <input
                    :id
                    ref="inputElement"
                    class="input"
                    :disabled
                    :name="id"
                    :step
                    type="range"
                    @change="onChange"
                    @input.prevent.stop="setValueFromInput"
                />

                <label
                    ref="labelElement"
                    v-qa="'value'"
                    class="label"
                    :class="classTwoKnobbed"
                    :data-qa-value="modelValue"
                    :for="id"
                    :style="styleLabel"
                >
                    {{ label }} {{ valueFormatter(valueUi) }}
                </label>
            </div>

            <div
                class="clamp-spacer right"
                :style="styleClampSpacerRight"
            ></div>
        </div>

        <label
            v-if="minLabel"
            class="value-label min"
        >
            {{ minLabel }}
        </label>

        <label
            v-if="maxLabel"
            class="value-label max"
        >
            {{ maxLabel }}
        </label>
    </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue'

import useInputRange from './useInputRange'
import { useInputRangeSynchronize } from './useInputRangeSynchronize'
import { useInputRangeStyle } from './useInputRangeStyle'
import { useInputRangeLazy } from './useInputRangeLazy'

interface Props {
    modelValue?: number
    min?: number
    max?: number
    clamp?: { min: number; max: number }
    minLabel?: string | number
    maxLabel?: string | number
    step?: number
    label?: string
    valueFormatter?: (value: number) => string
    id: string
    isPartOfTwoKnobbed?: boolean
    lazy?: boolean
    disabled?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    modelValue: 0,
    min: 0,
    clamp: undefined,
    minLabel: undefined,
    maxLabel: undefined,
    max: 100,
    step: 1,
    label: undefined,
    valueFormatter: (value: number) => String(value),
    isPartOfTwoKnobbed: false,
    lazy: false,
})

const emit = defineEmits<{
    'update:modelValue': [number]
}>()

const {
    minFinal,
    maxFinal,
    rangeTotal,
    rangeClamped,
    clampPercentLeft,
    clampPercentRight,
    styleClampSpacerLeft,
    styleClampSpacerRight,
} = useInputRange(props)

const val = computed({
    get(): number {
        return props.modelValue
    },
    set(val: number) {
        emit('update:modelValue', Number(val))
    },
})

const { valueUi, onChange } = useInputRangeLazy(val, props.lazy)

const { setValueFromInput, inputElement } = useInputRangeSynchronize(
    val,
    valueUi,
    minFinal,
    maxFinal
)

const { thumbDiameterCss, styleLabel, classTwoKnobbed, labelElement } =
    useInputRangeStyle(
        rangeClamped,
        rangeTotal,
        valueUi,
        minFinal,
        clampPercentLeft,
        clampPercentRight,
        props
    )

const classInputRange = computed(() => ({
    disabled: props.disabled,
}))
</script>

<style lang="scss" scoped>
.input-range {
    position: relative;
    height: 54px;

    &.disabled {
        .label {
            background-color: $grey-400 !important;
        }

        input[type='range'] {
            &::-webkit-slider-runnable-track {
                cursor: not-allowed !important;
            }

            &::-moz-range-track {
                cursor: not-allowed !important;
            }

            &::-webkit-slider-thumb {
                box-shadow: none !important;
                border-color: $grey-400 !important;
                cursor: not-allowed !important;
            }

            &::-moz-range-thumb {
                box-shadow: none !important;
                border-color: $grey-400 !important;
                cursor: not-allowed !important;
            }
        }
    }

    .track-container {
        height: 54px;
        display: flex;

        .clamp-spacer {
            margin-top: 25px;
            height: 4px;
            box-shadow: inset 0 1px 1px rgb(0 0 0 / 10%);
            background: $grey-100;
            border-radius: 2px;

            &.left {
                flex: 0 0 0%;
                border-top-right-radius: 0;
                border-bottom-right-radius: 0;
            }

            &.right {
                flex: 0 0 0%;
                border-top-left-radius: 0;
                border-bottom-left-radius: 0;
            }
        }

        .input-wrapper {
            flex: 1 1 auto;
            position: relative;

            .input {
                position: absolute;
                width: 100%;
                top: 16px;
            }

            @mixin track() {
                width: 100%;
                height: 4px;
                box-shadow: inset 0 1px 1px rgb(0 0 0 / 10%);
                background: $grey-300;
                border-radius: 2px;
                cursor: pointer;
            }

            @mixin thumb() {
                box-sizing: border-box;
                appearance: none;
                width: v-bind(thumbDiameterCss);
                height: v-bind(thumbDiameterCss);
                border: 7px solid $slate;
                border-radius: 50%;
                background-color: $white;
                cursor: pointer;
                box-shadow: 0 0 0 5px rgba($black, 0.1);

                &:active,
                &:focus {
                    outline: 5px solid color.adjust($slate, $alpha: -0.9);
                }
            }

            input[type='range'] {
                &::-webkit-slider-runnable-track {
                    margin-top: 9px;

                    @include track;
                }

                &::-moz-range-track {
                    @include track;
                }

                &::-webkit-slider-thumb {
                    margin-top: -7px;

                    @include thumb;
                }

                &::-moz-range-thumb {
                    @include thumb;
                }
            }

            &.two-knobbed {
                input[type='range'] {
                    pointer-events: none;

                    @mixin trackHidden() {
                        background: none;
                        box-shadow: none;
                    }

                    @mixin thumbHidden() {
                        pointer-events: auto;
                    }

                    &::-webkit-slider-runnable-track {
                        @include trackHidden;
                    }

                    &::-moz-range-track {
                        @include trackHidden;
                    }

                    &::-webkit-slider-thumb {
                        @include thumbHidden;
                    }

                    &::-moz-range-thumb {
                        @include thumbHidden;
                    }
                }
            }
        }
    }

    .label,
    .value-label {
        position: absolute;
        font-family: $effra-font-stack;
        font-weight: 400;
        font-size: 0.875rem;
        line-height: 0.875rem;
        text-align: center;
        letter-spacing: 0.05em;
        text-transform: uppercase;
    }

    .label {
        top: 0;
        white-space: nowrap;

        &.two-knobbed {
            top: -16px;
            padding: 6px;
            color: $white;
            background-color: $slate;
            border-radius: 4px;
        }
    }

    .value-label {
        font-weight: 400;
        bottom: 0;

        &.min {
            left: 0;
        }

        &.max {
            right: 0;
        }
    }
}
</style>
